"use client";

import { useState, useMemo, useEffect } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import Image from "next/image";
import { useUser, useSignIn } from "@clerk/nextjs";
import styles from "./help.module.css";

import { DOCS_STRUCTURE } from "./docsData";
import ArticleCodeBlock from "./ArticleCodeBlock";
import { renderArticleImage } from "./renderMarkdown";


export default function HelpCenterPage() {
  const router = useRouter();
  // Clerk hooks: useUser tracks the signed-in state reactively (covers what
  // the old onAuthStateChange listener did); useSignIn drives the modal
  // login flow with the same migration-check fallback as /login.
  const { isSignedIn, isLoaded: userLoaded } = useUser();
  const { signIn, setActive, isLoaded: signInLoaded } = useSignIn();
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [expandedSections, setExpandedSections] = useState(
    DOCS_STRUCTURE.map((s) => s.id)
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(null); // 'yes' | 'no' | null
  const [sessionId, setSessionId] = useState(null);

  // Login modal state
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);

  // Derive auth state from Clerk's reactive hook. Treat the pre-load phase
  // as "not signed in" to preserve the prior "show modal until proven
  // authenticated" behavior.
  const isAuthenticated = userLoaded ? Boolean(isSignedIn) : false;

  const handleContactSupport = () => {
    if (isAuthenticated) {
      router.push("/support");
    } else {
      setShowLoginModal(true);
      setLoginError("");
      setLoginEmail("");
      setLoginPassword("");
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError("");
    if (!signInLoaded) return;
    setLoginLoading(true);

    try {
      const result = await signIn.create({
        identifier: loginEmail,
        password: loginPassword,
        strategy: "password",
      });

      if (result.status === "complete") {
        await setActive({ session: result.createdSessionId });
        setShowLoginModal(false);
        router.push("/support");
        return;
      }

      // Edge case: non-complete status (e.g., second-factor). MFA not supported.
      console.warn("[help] Unexpected non-complete signIn status:", result.status);
      setLoginError("Unable to sign in. Please contact support if this persists.");
    } catch (err) {
      const clerkError = err?.errors?.[0];
      const code = clerkError?.code;

      // Mirror /login's lazy GoTrue→Clerk migration trigger: if the user
      // isn't in Clerk yet, check Prisma and, if they exist, provision
      // their Clerk account and send a password-setup email.
      if (code === "form_identifier_not_found") {
        try {
          const checkResp = await fetch("/api/auth/migration-check", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ email: loginEmail }),
          });
          const checkData = await checkResp.json();

          if (checkData?.migrationNeeded) {
            try {
              await signIn.create({
                strategy: "reset_password_email_code",
                identifier: loginEmail,
              });
            } catch (resetErr) {
              console.error("[help] Failed to send reset email post-migration:", resetErr);
            }
            setLoginError(checkData.message);
            return;
          }
        } catch (checkErr) {
          console.error("[help] migration-check failed:", checkErr);
        }

        setLoginError("Invalid email or password.");
        return;
      }

      if (code === "form_password_incorrect" || code === "form_password_invalid") {
        setLoginError("Invalid email or password.");
        return;
      }

      console.error("Login error:", err);
      setLoginError(clerkError?.message || "Sign in failed. Please try again.");
    } finally {
      setLoginLoading(false);
    }
  };

  // Generate/retrieve session ID for anonymous feedback tracking
  useEffect(() => {
    if (typeof window === "undefined") return;
    let storedSessionId = sessionStorage.getItem("help-session-id");
    if (!storedSessionId) {
      storedSessionId = `sess-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      sessionStorage.setItem("help-session-id", storedSessionId);
    }
    setSessionId(storedSessionId);
  }, []);

  // Handle URL article parameter
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const articleId = params.get("article");
    if (articleId) {
      for (const section of DOCS_STRUCTURE) {
        const found = section.articles.find((a) => a.id === articleId);
        if (found) {
          setSelectedArticle({ ...found, sectionId: section.id, sectionTitle: section.title });
          break;
        }
      }
    }
  }, []);

  // Reset feedback when article changes
  useEffect(() => {
    setFeedbackSubmitted(null);
  }, [selectedArticle?.id]);

  // The article body renders via dangerouslySetInnerHTML, which the
  // browser refuses to execute <script> tags from for security. So pull
  // the script srcs out of the rendered article and append them as real
  // <script> elements. customElements + the worker dedupe everything else.
  useEffect(() => {
    // Fenced examples are copyable text, not live embeds to load on this page.
    const content = (selectedArticle?.content || "").replace(/```[\s\S]*?(?:```|$)/g, "");
    const srcs = [...content.matchAll(/<script[^>]*\bsrc=["']([^"']+)["']/g)].map((m) => m[1]);
    if (srcs.length === 0) return;

    const appended = srcs
      .filter((src) => !document.querySelector(`script[src="${src}"]`))
      .map((src) => {
        const s = document.createElement("script");
        s.src = src;
        s.async = true;
        // Anything from flarefasthost.com is built as ESM (component.js
        // and the per-video data modules); embed.flare.link/embed.js is
        // the legacy classic helper.
        if (src.includes("flarefasthost.com")) s.type = "module";
        document.body.appendChild(s);
        return s;
      });

    return () => appended.forEach((s) => s.remove());
  }, [selectedArticle?.id, selectedArticle?.content]);

  const submitFeedback = async (isHelpful) => {
    if (!selectedArticle?.id) return;

    setFeedbackSubmitted(isHelpful ? "yes" : "no");

    try {
      await fetch("/api/help/feedback", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          articleId: selectedArticle.id,
          isHelpful,
          sessionId,
        }),
      });
    } catch (error) {
      console.error("Failed to submit feedback:", error);
    }
  };

  const toggleSection = (sectionId) => {
    setExpandedSections((prev) =>
      prev.includes(sectionId)
        ? prev.filter((id) => id !== sectionId)
        : [...prev, sectionId]
    );
  };

  const handleBackToHome = () => {
    setSelectedArticle(null);
    if (typeof window !== "undefined") {
      window.history.pushState({}, "", "/help");
    }
  };

  const filteredStructure = useMemo(() => {
    if (!searchQuery.trim()) return DOCS_STRUCTURE;
    const query = searchQuery.toLowerCase();
    return DOCS_STRUCTURE.map((section) => ({
      ...section,
      articles: section.articles.filter(
        (article) =>
          article.title.toLowerCase().includes(query) ||
          article.content.toLowerCase().includes(query)
      ),
    })).filter((section) => section.articles.length > 0);
  }, [searchQuery]);

  const renderContent = (content) => {
    const lines = content.trim().split("\n");
    const elements = [];
    let currentList = [];
    let listType = null;
    let inTable = false;
    let tableRows = [];
    let htmlBlock = [];
    let htmlBlockDepth = 0;
    let inCode = false;
    let codeLines = [];
    let codeLanguage = "";
    const voidTags = new Set(["area", "base", "br", "col", "embed", "hr", "img", "input", "link", "meta", "param", "source", "track", "wbr"]);

    const flushList = () => {
      if (currentList.length > 0) {
        if (listType === "ul") {
          elements.push(
            <ul key={`list-${elements.length}`} className={styles.contentList}>
              {currentList}
            </ul>
          );
        } else {
          elements.push(
            <ol key={`list-${elements.length}`} className={styles.contentOrderedList}>
              {currentList}
            </ol>
          );
        }
        currentList = [];
        listType = null;
      }
    };

    const flushHtmlBlock = () => {
      if (htmlBlock.length > 0) {
        const html = htmlBlock.join("\n").replace(/<script[^>]*>[\s\S]*?<\/script>/gi, "");
        if (html.trim()) {
          elements.push(
            <div key={`html-${elements.length}`} dangerouslySetInnerHTML={{ __html: html }} />
          );
        }
        htmlBlock = [];
        htmlBlockDepth = 0;
      }
    };

    const flushTable = () => {
      if (tableRows.length > 0) {
        const headerRow = tableRows[0];
        const bodyRows = tableRows.slice(2); // Skip header and separator
        elements.push(
          <div key={`table-${elements.length}`} className={styles.tableWrapper}>
            <table className={styles.contentTable}>
              <thead>
                <tr>
                  {headerRow.map((cell, i) => (
                    <th key={i}>{cell}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {bodyRows.map((row, i) => (
                  <tr key={i}>
                    {row.map((cell, j) => (
                      <td key={j}>{formatInline(cell)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
        tableRows = [];
        inTable = false;
      }
    };

    const formatInline = (text) => {
      const parts = text.split(/(\*\*[^*]+\*\*|`[^`]+`|\[[^\]]+\]\([^)]+\))/g);
      return parts.map((part, i) => {
        if (part.startsWith("**") && part.endsWith("**")) {
          return <strong key={i}>{part.slice(2, -2)}</strong>;
        }
        if (part.startsWith("`") && part.endsWith("`")) {
          return <code key={i} className={styles.inlineCode}>{part.slice(1, -1)}</code>;
        }
        const linkMatch = part.match(/\[([^\]]+)\]\(([^)]+)\)/);
        if (linkMatch) {
          return (
            <Link key={i} href={linkMatch[2]} className={styles.contentLink}>
              {linkMatch[1]}
            </Link>
          );
        }
        return part;
      });
    };

    lines.forEach((line, index) => {
      const trimmed = line.trim();

      // Handle examples before HTML blocks so script tags stay visible as text.
      if (trimmed.startsWith("```")) {
        flushList();
        flushTable();
        flushHtmlBlock();
        if (inCode) {
          elements.push(<ArticleCodeBlock key={`code-${index}`} code={codeLines.join("\n")} language={codeLanguage} />);
          codeLines = [];
        } else {
          codeLanguage = trimmed.slice(3).trim();
        }
        inCode = !inCode;
        return;
      }
      if (inCode) {
        codeLines.push(line);
        return;
      }

      const articleImage = renderArticleImage(trimmed, styles, index);
      if (articleImage) {
        flushList();
        flushTable();
        flushHtmlBlock();
        elements.push(articleImage);
        return;
      }

      // Table detection
      if (trimmed.startsWith("|") && trimmed.endsWith("|")) {
        flushList();
        const cells = trimmed.slice(1, -1).split("|").map((c) => c.trim());
        if (!cells.every((c) => /^[-:]+$/.test(c))) {
          tableRows.push(cells);
        } else {
          tableRows.push(cells); // separator row
        }
        inTable = true;
        return;
      } else if (inTable) {
        flushTable();
      }

      // HTML block detection
      if (htmlBlockDepth > 0 || (trimmed.startsWith("<") && !trimmed.startsWith("<strong") && !trimmed.startsWith("<code") && /^<[a-z]/.test(trimmed))) {
        flushList();
        flushTable();
        htmlBlock.push(line);
        const openTags = (trimmed.match(/<([a-z][a-z0-9-]*)(?=[\s>]|$)/gi) || [])
          .filter((tag) => !voidTags.has(tag.slice(1).toLowerCase()))
          .length;
        const closeTags = (trimmed.match(/<\/[a-z][a-z0-9-]*>/gi) || []).length;
        const selfClosing = (trimmed.match(/\/>/g) || []).length;
        htmlBlockDepth += openTags - closeTags - selfClosing;
        if (htmlBlockDepth <= 0) {
          flushHtmlBlock();
        }
        return;
      }

      if (trimmed.startsWith("# ")) {
        flushList();
        elements.push(
          <h1 key={index} className={styles.contentH1}>{trimmed.slice(2)}</h1>
        );
      } else if (trimmed.startsWith("## ")) {
        flushList();
        elements.push(
          <h2 key={index} className={styles.contentH2}>{trimmed.slice(3)}</h2>
        );
      } else if (trimmed.startsWith("### ")) {
        flushList();
        elements.push(
          <h3 key={index} className={styles.contentH3}>{trimmed.slice(4)}</h3>
        );
      } else if (trimmed.startsWith("- ") || trimmed.startsWith("* ")) {
        if (listType !== "ul") {
          flushList();
          listType = "ul";
        }
        currentList.push(<li key={index}>{formatInline(trimmed.slice(2))}</li>);
      } else if (/^\d+\.\s/.test(trimmed)) {
        if (listType !== "ol") {
          flushList();
          listType = "ol";
        }
        currentList.push(<li key={index}>{formatInline(trimmed.replace(/^\d+\.\s/, ""))}</li>);
      } else if (trimmed === "") {
        flushList();
      } else {
        flushList();
        elements.push(
          <p key={index} className={styles.contentP}>{formatInline(trimmed)}</p>
        );
      }
    });

    flushList();
    flushTable();
    flushHtmlBlock();
    if (codeLines.length > 0) {
      elements.push(<ArticleCodeBlock key="code-final" code={codeLines.join("\n")} language={codeLanguage} />);
    }
    return elements;
  };

  return (
    <div className={styles.layout}>
      {/* Header */}
      <header className={styles.header}>
        <div className={styles.headerInner}>
          <div className={styles.headerLeft}>
            <button
              className={styles.mobileMenuBtn}
              onClick={() => setMobileNavOpen(!mobileNavOpen)}
              aria-label="Toggle navigation"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 12h18M3 6h18M3 18h18" />
              </svg>
            </button>
            <Link href="/" className={styles.logoLink}>
              <Image src="/logoFlare.png" alt="Flare" width={80} height={18} priority quality={100} />
            </Link>
            <span className={styles.headerDivider}>/</span>
            <span className={styles.headerTitle}>App Docs</span>
          </div>
          <div className={styles.headerRight}>
            <Link href="/help/api" className={styles.headerLinkSubtle}>API Docs</Link>
            <Link href="/login" className={styles.headerLinkSubtle}>Sign in</Link>
            <Link href="/register" className={styles.headerButtonPrimary}>Get Started</Link>
          </div>
        </div>
      </header>

      <div className={styles.main}>
        {/* Sidebar */}
        <aside className={`${styles.sidebar} ${mobileNavOpen ? styles.sidebarOpen : ""}`}>
          <div className={styles.sidebarSearch}>
            <svg className={styles.searchIcon} width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5">
              <circle cx="6" cy="6" r="4.5" />
              <path d="M10 10l3 3" />
            </svg>
            <input
              type="text"
              placeholder="Search docs..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={styles.searchInput}
            />
          </div>

          <nav className={styles.nav}>
            {filteredStructure.map((section) => (
              <div key={section.id} className={styles.navSection}>
                <button
                  className={styles.navSectionHeader}
                  onClick={() => toggleSection(section.id)}
                >
                  <span>{section.title}</span>
                  <svg
                    className={`${styles.chevron} ${expandedSections.includes(section.id) ? styles.chevronOpen : ""}`}
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="m9 18 6-6-6-6" />
                  </svg>
                </button>
                {expandedSections.includes(section.id) && (
                  <ul className={styles.navList}>
                    {section.articles.map((article) => (
                      <li key={article.id}>
                        <Link
                          href={`/help/${article.id}`}
                          className={`${styles.navItem} ${selectedArticle?.id === article.id ? styles.navItemActive : ""}`}
                          onClick={() => setMobileNavOpen(false)}
                        >
                          {article.title}
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </nav>
        </aside>

        {/* Content */}
        <main className={styles.content}>
          {selectedArticle ? (
            <article className={styles.article}>
              <div className={styles.breadcrumb}>
                <button onClick={handleBackToHome} className={styles.breadcrumbLink}>App Docs</button>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="m9 18 6-6-6-6" />
                </svg>
                <span>{selectedArticle.sectionTitle}</span>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="m9 18 6-6-6-6" />
                </svg>
                <span>{selectedArticle.title}</span>
              </div>
              <div className={styles.articleContent}>
                {renderContent(selectedArticle.content)}
              </div>
              <footer className={styles.articleFooter}>
                {feedbackSubmitted ? (
                  <p className={styles.feedbackThanks}>Thanks for your feedback!</p>
                ) : (
                  <>
                    <p>Was this helpful?</p>
                    <div className={styles.feedbackBtns}>
                      <button
                        className={styles.feedbackBtn}
                        onClick={() => submitFeedback(true)}
                      >
                        Yes
                      </button>
                      <button
                        className={styles.feedbackBtn}
                        onClick={() => submitFeedback(false)}
                      >
                        No
                      </button>
                    </div>
                  </>
                )}
              </footer>
            </article>
          ) : (
            <div className={styles.home}>
              <h1 className={styles.homeTitle}>Flare App Documentation</h1>
              <p className={styles.homeSubtitle}>
                Learn how to record, edit, and share videos with the Flare app.
              </p>
              <div className={styles.homeCallout}>
                <div>
                  <div className={styles.homeCalloutTitle}>Looking for the API?</div>
                  <div className={styles.homeCalloutText}>
                    Use personal tokens to automate uploads, embeds, and video metadata.
                  </div>
                </div>
                <Link href="/help/api" className={styles.homeCalloutLink}>
                  View API Docs
                </Link>
              </div>

              <div className={styles.homeGrid}>
                {DOCS_STRUCTURE.map((section) => (
                  <div key={section.id} className={styles.homeCard}>
                    <h2 className={styles.homeCardTitle}>{section.title}</h2>
                    <ul className={styles.homeCardList}>
                      {section.articles.slice(0, 3).map((article) => (
                        <li key={article.id}>
                          <Link
                            href={`/help/${article.id}`}
                            className={styles.homeCardLink}
                          >
                            {article.title}
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="m9 18 6-6-6-6" />
                            </svg>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>

              <div className={styles.homeContact}>
                <h2>Need more help?</h2>
                <p>Our support team is ready to assist you.</p>
                <button onClick={handleContactSupport} className={styles.contactBtn}>
                  Contact Support
                </button>
              </div>
            </div>
          )}
        </main>
      </div>

      {/* Login Modal */}
      {showLoginModal && (
        <div
          className={styles.modalBackdrop}
          onClick={() => setShowLoginModal(false)}
          role="presentation"
        >
          <div
            className={styles.loginModal}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="login-modal-title"
          >
            <button
              className={styles.modalCloseBtn}
              onClick={() => setShowLoginModal(false)}
              aria-label="Close"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>

            <div className={styles.loginModalHeader}>
              <h2 id="login-modal-title" className={styles.loginModalTitle}>Sign in to continue</h2>
              <p className={styles.loginModalSubtitle}>
                Log in to access the Support Center and submit a request.
              </p>
            </div>

            {loginError && (
              <div className={styles.loginError}>{loginError}</div>
            )}

            <form onSubmit={handleLogin} className={styles.loginForm}>
              <div className={styles.loginFormGroup}>
                <label className={styles.loginLabel} htmlFor="login-email">Email</label>
                <input
                  id="login-email"
                  type="email"
                  className={styles.loginInput}
                  value={loginEmail}
                  onChange={(e) => setLoginEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  autoComplete="email"
                  autoFocus
                />
              </div>

              <div className={styles.loginFormGroup}>
                <label className={styles.loginLabel} htmlFor="login-password">Password</label>
                <input
                  id="login-password"
                  type="password"
                  className={styles.loginInput}
                  value={loginPassword}
                  onChange={(e) => setLoginPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  autoComplete="current-password"
                />
              </div>

              <button
                type="submit"
                className={styles.loginSubmitBtn}
                disabled={loginLoading}
              >
                {loginLoading ? "Signing in..." : "Sign in"}
              </button>
            </form>

            <div className={styles.loginModalFooter}>
              <p>
                Don't have an account?{" "}
                <Link href="/register" className={styles.loginLink}>
                  Sign up
                </Link>
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
